import api from './api'

export const taskService = {
  getTasks: async (projectId, params = {}) => {
    const response = await api.get(`/projects/${projectId}/tasks`, { params })
    return response.data
  },

  getTask: async (projectId, taskId) => {
    const response = await api.get(`/projects/${projectId}/tasks/${taskId}`)
    return response.data
  },

  // "PC-42" → full task (same payload as getTask)
  getTaskByKey: async (key) => {
    const response = await api.get(`/tasks/by-key/${encodeURIComponent(key)}`)
    return response.data
  },

  createTask: async (projectId, data) => {
    const response = await api.post(`/projects/${projectId}/tasks`, data)
    return response.data
  },

  updateTask: async (projectId, taskId, data) => {
    const response = await api.put(`/projects/${projectId}/tasks/${taskId}`, data)
    return response.data
  },

  deleteTask: async (projectId, taskId) => {
    const response = await api.delete(`/projects/${projectId}/tasks/${taskId}`)
    return response.data
  },

  reorderTasks: async (projectId, tasks) => {
    const response = await api.put(`/projects/${projectId}/tasks/reorder`, { tasks })
    return response.data
  },

  // Issue links ("blocks", "relates to", ...)
  getLinks: async (projectId, taskId) => {
    const response = await api.get(`/projects/${projectId}/tasks/${taskId}/links`)
    return response.data
  },

  addLink: async (projectId, taskId, linkType, target) => {
    const response = await api.post(`/projects/${projectId}/tasks/${taskId}/links`, { link_type: linkType, target })
    return response.data
  },

  removeLink: async (projectId, taskId, linkId) => {
    const response = await api.delete(`/projects/${projectId}/tasks/${taskId}/links/${linkId}`)
    return response.data
  },

  // JQL-lite search across every project you can see: { tasks, total, limit, offset }
  searchJql: async (jql, { limit = 50, offset = 0, includeArchived = false } = {}) => {
    const response = await api.get('/tasks/search', { params: { jql, limit, offset, ...(includeArchived && { include_archived: true }) } })
    return response.data
  },

  // One change applied to many tasks of a project: { updated: [...], failed: [...] }
  bulkUpdate: async (projectId, ids, set) => {
    const response = await api.post(`/projects/${projectId}/tasks/bulk`, { ids, set })
    return response.data
  },

  // Watchers (people who get the task's comments and status changes)
  getWatchers: async (projectId, taskId) => {
    const response = await api.get(`/projects/${projectId}/tasks/${taskId}/watchers`)
    return response.data
  },

  addWatcher: async (projectId, taskId, userId) => {
    const response = await api.post(`/projects/${projectId}/tasks/${taskId}/watchers`, userId ? { user_id: userId } : {})
    return response.data
  },

  removeWatcher: async (projectId, taskId, userId) => {
    const response = await api.delete(`/projects/${projectId}/tasks/${taskId}/watchers/${userId}`)
    return response.data
  },

  // Labels used in a project (most used first)
  getProjectLabels: async (projectId) => {
    const response = await api.get(`/projects/${projectId}/labels`)
    return response.data
  },

  getTaskTypes: async () => {
    const response = await api.get('/tasks/types')
    return response.data
  },

  // Comments
  addComment: async (projectId, taskId, content, parentId = null) => {
    const body = { content }
    if (parentId) body.parent_id = parentId
    const response = await api.post(`/projects/${projectId}/tasks/${taskId}/comments`, body)
    return response.data
  },

  updateComment: async (projectId, taskId, commentId, content) => {
    const response = await api.put(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`, { content })
    return response.data
  },

  deleteComment: async (projectId, taskId, commentId) => {
    const response = await api.delete(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`)
    return response.data
  },

  // Attachments
  uploadAttachments: async (projectId, taskId, files) => {
    const formData = new FormData()
    files.forEach(f => formData.append('files', f))
    const response = await api.post(`/projects/${projectId}/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  deleteAttachment: async (projectId, taskId, attachmentId) => {
    const response = await api.delete(`/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`)
    return response.data
  },

  deleteAttachmentByPath: async (projectId, taskId, filePath) => {
    const response = await api.delete(`/projects/${projectId}/tasks/${taskId}/attachments/by-path`, { data: { filePath } })
    return response.data
  },

  // Extracted text for previews (PDF text, Word/Excel as Markdown, text files, ZIP listing)
  getAttachmentText: async (projectId, taskId, attachmentId) => {
    const response = await api.get(`/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}/text`)
    return response.data
  },
}
